import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import type { ChartOptions } from 'chart.js';

export interface MonthData {
  earnedTotalMonth: number;
  workingDaysTotalMonth: number;
  workingHoursTotalMonth: number;
  flexitimeHoursTotalMonth: number;
  overtimeHoursTotalMonth: number;
}

export interface YearDataType {
  earnedTotal: number;
  workingDaysTotal: number;
  workingHoursTotal: number;
  flexitimeHoursTotal: number;
  overtimeHoursTotal: number;
  jan: MonthData;
  feb: MonthData;
  mar: MonthData;
  apr: MonthData;
  may: MonthData;
  jun: MonthData;
  jul: MonthData;
  aug: MonthData;
  sep: MonthData;
  oct: MonthData;
  nov: MonthData;
  dec: MonthData;
}

type YearStatsGraphProps = {
  yearData: YearDataType | undefined;
  isAccumulated?: boolean;
  isPerMonth?: boolean;
};

export const YearStatsGraph = ({ yearData, isAccumulated = false, isPerMonth = false }: YearStatsGraphProps) => {
  if (!yearData) return <></>;

  // Get current month (0-based index)
  const currentMonth = new Date().getMonth();

  // Custom plugin to draw vertical line
  const verticalLinePlugin = {
    id: 'verticalLine',
    afterDraw: (chart: any) => {
      const ctx = chart.ctx;
      const xAxis = chart.scales.x;
      const yAxis = chart.scales.y;

      // Calculate x position for current month (0-based index)
      const xPos = xAxis.getPixelForValue(currentMonth);

      // Draw vertical line
      ctx.save();
      ctx.strokeStyle = 'red';
      ctx.lineWidth = 3;
      ctx.setLineDash([8, 4]);
      ctx.beginPath();
      ctx.moveTo(xPos, yAxis.top);
      ctx.lineTo(xPos, yAxis.bottom);
      ctx.stroke();
      ctx.restore();

      // Add label
      ctx.save();
      ctx.fillStyle = 'red';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Current Date', xPos, yAxis.top - 15);
      ctx.restore();
    },
  };

  const calculateData = () => {
    let accumulated = 0;
    const retValue = [
      {
        name: 'Jan',
        earned: yearData?.jan?.earnedTotalMonth,
        workingdays: yearData?.jan?.earnedTotalMonth,
      },
      {
        name: 'Feb',
        earned: yearData?.feb?.earnedTotalMonth,
        workingdays: yearData?.feb?.earnedTotalMonth,
      },
      {
        name: 'Mar',
        earned: yearData?.mar?.earnedTotalMonth,
        workingdays: yearData?.mar?.earnedTotalMonth,
      },
      {
        name: 'Apr',
        earned: yearData?.apr?.earnedTotalMonth,
        workingdays: yearData?.apr?.earnedTotalMonth,
      },
      {
        name: 'May',
        earned: yearData?.may?.earnedTotalMonth,
        workingdays: yearData?.may?.earnedTotalMonth,
      },
      {
        name: 'Jun',
        earned: yearData?.jun?.earnedTotalMonth,
        workingdays: yearData?.jun?.earnedTotalMonth,
      },
      {
        name: 'Jul',
        earned: yearData?.jul?.earnedTotalMonth,
        workingdays: yearData?.jul?.earnedTotalMonth,
      },
      {
        name: 'Aug',
        earned: yearData?.aug?.earnedTotalMonth,
        workingdays: yearData?.aug?.earnedTotalMonth,
      },
      {
        name: 'Sep',
        earned: yearData?.sep?.earnedTotalMonth,
        workingdays: yearData?.sep?.earnedTotalMonth,
      },
      {
        name: 'Oct',
        earned: yearData?.oct?.earnedTotalMonth,
        workingdays: yearData?.oct?.earnedTotalMonth,
      },
      {
        name: 'Nov',
        earned: yearData?.nov?.earnedTotalMonth,
        workingdays: yearData?.nov?.earnedTotalMonth,
      },
      {
        name: 'Dec',
        earned: yearData?.dec?.earnedTotalMonth,
        workingdays: yearData?.dec?.earnedTotalMonth,
      },
    ];
    return retValue.map((item) => {
      accumulated += item.earned;
      return {
        ...item,
        earnedAccumulated: accumulated,
      };
    });
  };

  const options = {
    responsive: true,
    scales: {
      x: {
        parsing: false,
        // type: 'line',
        // time: {
        //   unit: 'day'
        // },
        // display: true,
        // title: {
        //   display: true,
        //   text: 'Date'
        // },
        ticks: {
          major: {
            // enabled: true
          },
          // color: '#FF0000'
        },
      },
      y: {
        // beginAtZero: true
        display: true,
        title: {
          display: true,
          text: 'Income',
        },
        ticks: {
          beginAtZero: true,
          max: 10,
          // stepSize: 100, // Set a fixed interval between ticks
          display: true, // Hide Y-axis labels
        },
      },
    },
    plugins: {
      legend: {
        position: 'top',
      },
      // title: {
      //   display: true,
      //   text: healthMetric?.description
      // }
    },
  } as ChartOptions<'line'>;

  const data = (compact = false) => {
    const tension = 0.1;
    const retVal: {
      labels: string[];
      datasets: {
        label: string;
        data: number[];
        borderColor: string;
        backgroundColor: string;
        tension: number;
        borderWidth: number;
        borderDash?: number[];
        segment?: {
          borderDash?: (ctx: any) => number[];
        };
      }[];
    } = {
      labels: calculateData?.()?.map(({ name }) => name),
      datasets: [],
    };

    if (isPerMonth)
      retVal?.datasets?.push({
        label: 'Per month',
        // yAxisID: 'y-axis-1', // Specify the ID of the Y-axis for this dataset
        data: calculateData?.()?.map(({ earned }) => earned),
        borderColor: 'blue',
        backgroundColor: 'blue',
        tension,
        borderWidth: compact ? 1 : 2,
        segment: {
          borderDash: (ctx) => {
            // Make line dashed after current month
            return ctx.p1DataIndex > currentMonth ? [5, 5] : [];
          },
        },
      });

    if (isAccumulated)
      retVal?.datasets?.push({
        label: 'Accumulated',
        // yAxisID: 'y-axis-2', // Specify the ID of the Y-axis for this dataset
        data: calculateData?.()?.map(({ earnedAccumulated }) => earnedAccumulated),
        borderColor: 'green',
        backgroundColor: 'green',
        tension,
        borderWidth: compact ? 1 : 2,
        segment: {
          borderDash: (ctx) => {
            // Make line dashed after current month
            return ctx.p1DataIndex > currentMonth ? [5, 5] : [];
          },
        },
      });

    return retVal;
  };

  return <Line options={options} data={data()} plugins={[verticalLinePlugin]} style={{ maxHeight: '400px' }} />;
};
